import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Globe, Eye, ClipboardCopy, Star } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Tooltip as ShadTooltip, TooltipTrigger as ShadTooltipTrigger, TooltipContent as ShadTooltipContent } from "@/components/ui/tooltip";
import { JobViewService } from "@/services/jobViewService";

interface Job {
  id: string;
  title: string;
  company: {
    id: string;
    name: string;
  };
  skills: string[];
  tags?: string[];
  matchCount?: number;
  contactId?: string;
  published?: boolean;
  applicationsCount?: number;
  processesCount?: number;
  status?: string;
  newApplicationsCount?: number;
}

interface JobCardProps {
  job: Job;
  className?: string;
  disableLinks?: boolean;
  isSelected?: boolean;
  isFavorite?: boolean;
}

export const JobCard = ({ job, className, disableLinks = false, isSelected = false, isFavorite = false }: JobCardProps) => {

  const { t } = useTranslation();
  const { toast } = useToast();

  const TitleComponent = disableLinks ? 'div' : Link;
  const CompanyComponent = disableLinks ? 'div' : Link;
  const ViewButtonComponent = disableLinks ? 'div' : Link;



  // Handle job click to mark as viewed
  const handleJobClick = async () => {
    if (!disableLinks) {
      try {
        await JobViewService.markJobAsViewed(job.id);
      } catch (error) {
        // Error is already logged in the service
      }
    }
  };

  // Check if job is closed
  const isClosed = job.status?.startsWith('closed/');

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'hold':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      case 'closed/won':
      case 'closed/lost':
        return 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
      default:
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
    }
  };

  return (
    <div
      className={cn(
        "relative flex flex-col h-full overflow-visible transition-all duration-300 hover:shadow-lg backdrop-blur-lg bg-white/80 dark:bg-black/60 border border-white/30 dark:border-white/20 rounded-sm",
        "hover:bg-white/90 dark:hover:bg-black/70 hover:scale-[1.02] hover:border-white/40 dark:hover:border-white/30",
        isSelected && "ring-2 ring-blue-500 border-blue-500",
        isClosed && "opacity-60 grayscale",
        className
      )}
      style={{paddingBottom: 0}}
    >
      {/* iOS-style notification badge in top right corner */}
      {job.newApplicationsCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-sm min-w-[24px] h-6 flex items-center justify-center px-1 z-10 font-bold shadow-lg">
              {job.newApplicationsCount > 99 ? '99+' : job.newApplicationsCount}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {t("jobs.newApplications", "New applications")}
          </TooltipContent>
        </Tooltip>
      )}
      <div className="p-4 border-b border-border/50 flex flex-col justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <TitleComponent 
            to={disableLinks ? undefined : `/jobs/${job.id}`}
            className={cn(
              "hover:text-primary transition-colors flex items-center gap-2 min-w-0 flex-1",
              disableLinks && "cursor-default"
            )}
            onClick={handleJobClick}
          >
            <ShadTooltip>
              <ShadTooltipTrigger asChild>
                <h3
                  className={cn(
                    "text-lg font-semibold truncate text-gray-900 dark:text-gray-100",
                    isClosed && "line-through text-gray-500"
                  )}
                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                >
                  {job.title}
                </h3>
              </ShadTooltipTrigger>
              <ShadTooltipContent side="top" sideOffset={8} style={{zIndex: 50}}>
                <span>{job.title}</span>
              </ShadTooltipContent>
            </ShadTooltip>
            {isFavorite && <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />}
          </TitleComponent>
          {job.published && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="text-green-600 hover:text-green-700 flex items-center transition-colors flex-shrink-0"
                  type="button"
                  title="Published - Click for options"
                >
                  <Globe className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-60 p-3" side="bottom" align="start">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-sm">Published Job</span>
                  </div>
                  <div className="space-y-2">
                    <button
                      className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                      onClick={() => {
                        window.open(`${window.location.origin}/jobs/${job.id}/public`, '_blank', 'noopener');
                      }}
                    >
                      <Eye className="h-4 w-4 text-blue-500" />
                      <div>
                        <div className="text-sm font-medium">View Public Page</div>
                        <div className="text-xs text-gray-500">Open in new tab</div>
                      </div>
                    </button>
                    <button
                      className="w-full flex items-center gap-3 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/jobs/${job.id}/public`);
                        toast({ 
                          title: 'Copied!', 
                          description: 'Public page URL copied to clipboard.' 
                        });
                      }}
                    >
                      <ClipboardCopy className="h-4 w-4 text-gray-600" />
                      <div>
                        <div className="text-sm font-medium">Copy Link</div>
                        <div className="text-xs text-gray-500">Share this job publicly</div>
                      </div>
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        <CompanyComponent 
          to={disableLinks ? undefined : `/companies/${job.company.id}`}
          className={cn(
            "text-sm text-blue-500 hover:underline dark:text-blue-400 mt-1",
            disableLinks && "cursor-default hover:no-underline"
          )}
        >
          {job.company.name}
        </CompanyComponent>
        {(job.status && !isClosed) || isClosed ? (
          <div className="flex items-center gap-2 mt-2">
            {job.status && !isClosed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={cn("text-xs px-2 py-1 rounded-sm", getStatusColor(job.status))}>
                    {String(t(`jobs.statuses.${job.status}`, job.status))}
                  </span>
                </TooltipTrigger>
                <TooltipContent>Job status: {String(t(`jobs.statuses.${job.status}`, job.status))}</TooltipContent>
              </Tooltip>
            )}
            {isClosed && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-sm">
                    Closed
                  </span>
                </TooltipTrigger>
                <TooltipContent>Job is closed</TooltipContent>
              </Tooltip>
            )}
          </div>
        ) : null}
      </div>
      <div className="flex-1 flex flex-col p-4 pb-0">
        {/* Applications & Processes counts as pills */}
        {/* Pills and View button at the bottom, always on one line, left-aligned with skills */}
        <div className="flex flex-row justify-between items-center pt-2 pb-0 mt-auto w-full">
          <div className="flex flex-row gap-2 flex-nowrap">
            <div
              className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold bg-emerald-50/30 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400"
            >
              <span>{job.applicationsCount ?? 0}</span>
              <span className="ml-1">Applications</span>
            </div>
            <div
              className="flex items-center gap-1 rounded-sm px-2 py-0.5 text-[11px] font-semibold bg-orange-50/30 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400"
            >
              <span>{job.processesCount ?? 0}</span>
              <span className="ml-1">Processes</span>
            </div>
          </div>
          <ViewButtonComponent
            to={disableLinks ? undefined : `/jobs/${job.id}`}
            className={cn(
              "inline-flex items-center justify-center px-3 py-0.5 text-sm font-medium rounded-sm bg-blue-500 text-white hover:bg-blue-500 transition-colors dark:bg-blue-500 dark:hover:bg-blue-500",
              disableLinks && "cursor-default hover:bg-blue-500 dark:hover:bg-blue-500"
            )}
            onClick={handleJobClick}
          >
            {t("contacts.view")}
          </ViewButtonComponent>
        </div>
        <div className="flex justify-between items-center mt-auto pt-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {job.matchCount !== undefined ? (
              <span><span className="font-medium">{job.matchCount}</span>{t("matches.potentialMatches")}</span>
            ) : (
              <span className="opacity-0">{t("matches.noMatchesFound")}</span>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};
